import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryScatter } from 'victory';
import DashboardService from 'api/dashboard/DashboardService';
import { Co2MonthlyEvolutionDashboardDto } from 'api/dashboard/dtos/Co2MonthlyEvolutionDashboardDto';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import dayjs from 'dayjs';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import openFuelCharges from '../utils/openFuelCharges';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './Co2MonthlyEvolution.module.scss';

const LINE_COLOR = '#02c7c7';
const AREA_COLOR = 'rgba(2, 199, 199, 0.15)';

const formatCo2 = (value: number) =>
	value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kg';

const formatLiters = (value: number) =>
	value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' L';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const Co2MonthlyEvolution = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<Co2MonthlyEvolutionDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('co2MonthlyEvolution');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);
	const [tooltip, setTooltip] = useState<{ label: string; co2: number; liters: number; x: number; y: number } | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getCo2MonthlyEvolution(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const chartData = useMemo(() => {
		if (!data) return [];
		return data.map(item => ({
			date: dayjs(item.date).startOf('month').toDate(),
			value: item.co2Kg,
			liters: item.totalLiters,
		}));
	}, [data]);

	const MAX_TICKS = 12;
	const TICK_STEPS = [1, 2, 3, 6, 12];
	const tickStep = TICK_STEPS.find(s => Math.ceil(chartData.length / s) <= MAX_TICKS) ?? 12;
	const tickValues = chartData.filter((_, i) => i % tickStep === 0).map(d => d.date);

	const periodTotal = (data || []).reduce((sum, item) => sum + (item.co2Kg || 0), 0);

	const goToFuelCharges = () => {
		openFuelCharges({
			dateFrom,
			dateTo,
			fuelTypeLabel: t('common.generic_types.FUEL' as any) as string,
		});
	};

	const goToMonthFuelCharges = (date: Date) => {
		const monthStart = dayjs(date).startOf('month').toDate();
		const monthEnd = dayjs(date).endOf('month').toDate();
		openFuelCharges({
			dateFrom: monthStart,
			dateTo: monthEnd,
			fuelTypeLabel: t('common.generic_types.FUEL' as any) as string,
		});
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.co2_monthly_evolution' as any) as string}
			subTitleTooltip={t((scopeVehicleId ? 'dashboard.co2_monthly_evolution_asset_tooltip' : 'dashboard.co2_monthly_evolution_tooltip') as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<KpiHeader
				value={formatCo2(periodTotal)}
				subLabel={t('dashboard.period_total' as any) as string}
				currentTotal={periodTotal}
				previousTotal={null}
				previousRange={null}
				onClick={goToFuelCharges}
			/>
			<div className={styles.chartContainer} ref={containerRef}>
				{isLoading ? (
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				) : chartData.length === 0 ? (
					<WidgetNoData />
				) : (
					width > 0 && height > 0 && (
						<VictoryChart
							width={width}
							height={height}
							domainPadding={{ x: Math.max(20, 40 - chartData.length * 2) }}
							padding={{ top: 20, bottom: 50, left: 70, right: 20 }}
						>
							<VictoryAxis
								scale="time"
								style={{
									axis: { stroke: 'transparent' },
									tickLabels: { fill: '#000000', userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
								}}
								tickValues={tickValues}
								tickFormat={x => dayjs(x).format('MMM YY')}
							/>
							<VictoryAxis
								dependentAxis
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								tickFormat={x => formatCo2(x)}
							/>
							<VictoryArea
								data={chartData}
								x="date"
								y="value"
								interpolation="monotoneX"
								style={{
									data: {
										fill: AREA_COLOR,
										stroke: LINE_COLOR,
										strokeWidth: 2,
									},
								}}
							/>
							<VictoryScatter
								data={chartData}
								x="date"
								y="value"
								size={4}
								style={{
									data: {
										fill: LINE_COLOR,
										stroke: '#fff',
										strokeWidth: 1.5,
										cursor: 'pointer',
									},
								}}
								// eslint-disable-next-line
								events={[{
									target: 'data' as const,
									eventHandlers: {
										onMouseEnter: (evt: any, targetProps: any) => {
											const datum = targetProps.datum;
											setTooltip({
												label: dayjs(datum.date).format('MMM YYYY'),
												co2: datum.value,
												liters: datum.liters,
												x: evt.clientX,
												y: evt.clientY,
											});
											return [];
										},
										onMouseMove: (evt: any) => {
											setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
											return [];
										},
										onMouseLeave: () => { setTooltip(null); return []; },
										onClick: (_evt: any, targetProps: any) => {
											goToMonthFuelCharges(targetProps.datum.date);
											return [];
										},
									},
								}]}
							/>
						</VictoryChart>
					)
				)}
				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
						<div className={styles.tooltipTitle}>{tooltip.label}</div>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: LINE_COLOR }} />
							<span className={styles.tooltipRowLabel}>{t('dashboard.co2_monthly_evolution_liters_label' as any)}:</span>
							<span className={styles.tooltipRowValue}>{formatLiters(tooltip.liters)}</span>
						</div>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: LINE_COLOR }} />
							<span className={styles.tooltipRowLabel}>{t('dashboard.co2_monthly_evolution_co2_label' as any)}:</span>
							<span className={styles.tooltipRowValue}>{formatCo2(tooltip.co2)}</span>
						</div>
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default Co2MonthlyEvolution;
